import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "ticket", "counter"]
  static values = {
    view: String // "index" o "bike_hunter"
  }

  connect() {
    console.log("📱 MobileTicketsController connected")
    
    this.isMobile = window.innerWidth < 768
    
    if (!this.isMobile) {
      console.log("💻 Desktop detected - mobile features disabled")
      return
    }

    this.currentTicketIndex = 0
    this.setupIntersectionObserver()
    this.restoreScrollPosition()
    this.setupHapticFeedback()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupIntersectionObserver() {
    const options = {
      root: this.hasContainerTarget ? this.containerTarget : null,
      rootMargin: "0px",
      threshold: 0.5 // 50% del ticket visible para considerarlo "activo"
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const ticketElement = entry.target
          const ticketId = ticketElement.dataset.ticketId
          
          // Actualizar índice actual
          const allTickets = this.hasTicketTargets ? this.ticketTargets : []
          this.currentTicketIndex = allTickets.indexOf(ticketElement)
          
          // Guardar en localStorage
          this.saveCurrentTicket(ticketId)
          
          // Actualizar contador si existe
          this.updateCounter()
          
          // Feedback háptico suave
          this.vibrateLight()
          
          console.log(`📌 Ticket activo: ${ticketId} (${this.currentTicketIndex + 1}/${allTickets.length})`)
        }
      })
    }, options)

    // Observar todos los tickets
    if (this.hasTicketTargets) {
      this.ticketTargets.forEach(ticket => {
        this.observer.observe(ticket)
      })
    }
  }

  setupHapticFeedback() {
    // Verificar soporte de Vibration API
    this.hasVibration = 'vibrate' in navigator
    
    if (!this.hasVibration) {
      console.log("⚠️ Vibration API not supported")
    }
  }

  vibrateLight() {
    if (this.hasVibration) {
      navigator.vibrate(5) // 5ms de vibración suave
    }
  }

  vibrateMedium() {
    if (this.hasVibration) {
      navigator.vibrate(10) // 10ms para acciones más importantes
    }
  }

  saveCurrentTicket(ticketId) {
    const storageKey = `bikehunter_last_ticket_${this.viewValue || 'index'}`
    localStorage.setItem(storageKey, ticketId)
  }

  restoreScrollPosition() {
    const storageKey = `bikehunter_last_ticket_${this.viewValue || 'index'}`
    const lastTicketId = localStorage.getItem(storageKey)
    
    if (!lastTicketId) {
      console.log("📍 No saved position - starting from top")
      return
    }

    // Buscar el ticket guardado
    const savedTicket = this.hasTicketTargets 
      ? this.ticketTargets.find(t => t.dataset.ticketId === lastTicketId)
      : null

    if (savedTicket) {
      console.log(`🔄 Restoring position to ticket: ${lastTicketId}`)
      
      // Scroll suave al ticket guardado
      setTimeout(() => {
        savedTicket.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'start'
        })
      }, 100)
    } else {
      console.log("⚠️ Saved ticket not found")
    }
  }

  updateCounter() {
    if (!this.hasCounterTarget) return
    
    const total = this.hasTicketTargets ? this.ticketTargets.length : 0
    const current = this.currentTicketIndex + 1
    
    this.counterTarget.textContent = `${current} / ${total}`
  }

  // Método público para navegar programáticamente
  goToTicket(index) {
    if (!this.hasTicketTargets) return
    
    const ticket = this.ticketTargets[index]
    if (ticket) {
      ticket.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'start'
      })
      this.vibrateMedium()
    }
  }

  nextTicket() {
    const nextIndex = Math.min(this.currentTicketIndex + 1, this.ticketTargets.length - 1)
    if (nextIndex !== this.currentTicketIndex) {
      this.goToTicket(nextIndex)
    } else {
      // Vibración de límite alcanzado
      if (this.hasVibration) {
        navigator.vibrate([10, 50, 10]) // Patrón doble
      }
    }
  }

  previousTicket() {
    const prevIndex = Math.max(this.currentTicketIndex - 1, 0)
    if (prevIndex !== this.currentTicketIndex) {
      this.goToTicket(prevIndex)
    } else {
      // Vibración de límite alcanzado
      if (this.hasVibration) {
        navigator.vibrate([10, 50, 10]) // Patrón doble
      }
    }
  }
}
